// This file should NOT import types from our codebase, the eden repo should be treated as if a 3rd party library
import type { GameSummary_0_1_0 } from '@api/eden/generated/outputTypes/game_summary_0_1_0';
import type { InferType } from 'yup';
import type { SabertoothEvent, StatDocument, StatElement, TeamStatDoc } from '@sabertooth/types';

import type { InputContentType, OutputContentType } from './content-types';
import type { AssetPlaybackDataSchema, streamAssetMetadataWithTimeStampsSchema } from './contentTypeSchema';
import type {
    APIAthleteProfileCareerStatsSchema,
    APIAthleteProfileClipsListSchema,
    APIAthleteProfileSchema,
    APIAthleteProfileSportAttributesSchema,
} from './contentTypeSchema/APIAthleteProfile';
import type { APIOpponentTeamSchema } from './contentTypeSchema/APIOpponentTeam';

export const NO_CONTENT = 'NO_CONTENT';

export type EdenSabertoothEvent = SabertoothEvent;
export type EdenStats = StatDocument;
export type EdenStatElement = StatElement;
export type EdenStatsDoc = TeamStatDoc;

export type EdenDateOrDateTime = { date: string } | { datetime: string };

export type EdenStatEdit = {
    team_id: string;
    player_id?: string;
    stat_category: string;
    stat_short_code: string;
    value: number;
};

export type EdenGameStreamEvent = {
    event_data: string; // this is a json
    id: string;
    sequence_number: number;
    stream_id: string;
};

type Defenders =
    | [
          {
              error: boolean;
              location: {
                  x: number;
                  y: number;
              };
              position: string;
          },
      ]
    | [];

enum HomeRunLocation {
    LEFT_FIELD = 'left_field',
    CENTER_FIELD = 'center_field',
    RIGHT_FIELD = 'right_field',
    IN_THE_PARK = 'in_the_park',
}

export type SprayChartData = {
    id: string;
    code: string;
    attributes: {
        playType: string;
        defenders: Defenders;
        playResult: string;
        hrLocation?: HomeRunLocation;
    };
    compactorAttributes: {
        stream: string;
    };
};

export type BattingSide = 'both' | 'left' | 'right';
export type ThrowingHand = 'left' | 'right';

export interface FamilyInfo {
    email: string;
    first_name?: string;
    last_name?: string;
    phone_number?: string;
}

export interface UserIdOrEmail {
    user_id: string | undefined;
    email: string | undefined;
}

export interface UserPlayerRelationship {
    user_id: string;
    player_id: string;
    relationship: 'family' | 'self';
}

export interface UserTeamAssociation {
    user_id: string;
    team_id: string;
    association: AssociationType;
}

export interface PatchPlayer {
    email?: string;
    first_name?: string;
    last_name?: string;
    number?: string;
}

export enum BatsFieldingPosition {
    PITCHER = 'P',
    CATCHER = 'C',
    FIRST_BASE = '1B',
    SECOND_BASE = '2B',
    THIRD_BASE = '3B',
    SHORTSTOP = 'SS',
    LEFT_FIELD = 'LF',
    CENTER_FIELD = 'CF',
    RIGHT_FIELD = 'RF',
    SHORT_FIELD = 'SF',
}

type BatsStartingLineupEntry = {
    player_id: string;
    fielding_position?: BatsFieldingPosition | null;
};
type BatsStartingLineup = {
    id: string;
    dh?: string | null;
    dh_batting_for?: string | null;
    entries?: BatsStartingLineupEntry[];
};
type APIBatsStartingLineup = { creator: string } & Omit<BatsStartingLineup, 'entries'> & {
        entries: BatsStartingLineupEntry[];
    };

export type AssociationType = 'manager' | 'family' | 'player' | 'fan';

export interface Team {
    id: string;
    name: string;
    sport: string;
    city?: string | null;
    state?: string | null;
    country?: string | null;
    age_group: string;
    season_name: string;
    season_year: number;
    competition_level: string;
    team_type: string;
    settings?: {
        scorekeeping?: {
            bats?: {
                innings_per_game: number;
                // There are additional unused fields here.
            };
        };
        maxpreps?: {
            school_id?: string;
            boxscore_sync?: boolean;
        };
    };
    paid_access_level?: string;
    user_team_associations?: AssociationType[];
    team_avatar_image?: string | null;
    team_player_count?: number | null; // number of active players on team
    organizations: {
        organization_id: string;
        status: string;
    }[];
    public_id?: string;
}

export type PatchTeamRollover = {
    old_team_id: string;
    users: {
        user_id: string;
        team_groups: AssociationType[];
    }[];
    players: string[];
};

export type MaxPrepsImportComponent = 'roster_only' | 'schedule_only' | undefined;

export type PostLocation =
    | { name: string; place_id?: undefined; coordinates?: undefined }
    | { name?: string; place_id: string; coordinates?: undefined }
    | { name?: string; place_id?: undefined; coordinates: { latitude: number; longitude: number } };

export type PatchLocation =
    | { name: string; place_id: null; coordinates: null }
    | { name?: string | null; place_id: string; coordinates?: null }
    | { name?: string | null; place_id: null; coordinates: { latitude: number; longitude: number } };

export type Organization = {
    id: string;
    name: string;
    type: string; // 'league' | 'tournament' | 'travel'
    status: string; // 'active' | 'blocked' | 'invited'
    sport: string;
    season_name: string;
    season_year: number;
    public_id?: string;

    city?: string | null;
    state?: string | null;
    country?: string | null;

    end_date?: string | null;
    start_date?: string | null;

    avatar_image?: string | null;
};

export type OrganizationWithRole = {
    organization: Omit<Organization, 'status'>;
    association: string; // 'admin' | 'scorekeeper'
    status: string; // 'active' | 'blocked' | 'invited'
    invite_code?: string | null;
};

export type OrganizationScheduleEventTeamInfo = {
    team_id: string;
    event_id: string;
};

export type OrganizationScheduleEvent = {
    id: string;
    status: string; // 'scheduled' | 'canceled'
    organization_id: string;
    initial_home_team?: OrganizationScheduleEventTeamInfo | null;
    initial_away_team?: OrganizationScheduleEventTeamInfo | null;
    start_ts: string;
    end_ts: string;
    arrival_ts?: string | null;
    timezone?: string | null;
    location?: Location;
};

export type PatchOrganizationScheduleEvent = {
    status?: 'scheduled' | 'canceled';
    initial_home_team_id?: string | null;
    initial_away_team_id?: string | null;
    location?: PatchLocation;
    game_time?: { start_ts: string; end_ts: string; timezone: string };
};

export type PostOrganizationScheduleEvent = Omit<
    OrganizationScheduleEvent,
    'status' | 'organization_id' | 'initial_home_team' | 'initial_away_team' | 'location'
> & {
    initial_home_team_id?: string | null;
    initial_away_team_id?: string | null;
    location?: PostLocation;
};

export type PostBulkOrganizationScheduleEvent = Omit<
    PostOrganizationScheduleEvent,
    'start_ts' | 'end_ts' | 'initial_home_team_id' | 'initial_away_team_id'
> & {
    initial_home_team_id: string;
    initial_away_team_id: string;
    start_ts: string;
    end_ts: string;
};

export type OrganizationScheduleEventBulkUploadTemplateData = {
    date: string;
    time: string;
    home: string;
    away: string;
    location: string;
    duration: string;
};
export type OrganizationScheduleEventBulkUploadTemplateField = {
    fieldName: string;
    title: string;
    required: boolean;
    description: string;
    defaultValue?: string;
};

export type OrganizationScheduleEventBulkUploadTemplate = {
    fields: Array<OrganizationScheduleEventBulkUploadTemplateField>;
    data: Array<OrganizationScheduleEventBulkUploadTemplateData>;
};

export type PostOrganizationScheduleEventBulkValidate = {
    timezone: string;
    data: Array<Record<string, string>>;
};

export interface OrganizationScheduleEventBulkValidateResult {
    headers: Array<OrganizationScheduleEventBulkUploadTemplateField>;
    data: OrganizationScheduleEventBulkValidateItem[];
}

export type OrganizationScheduleEventBulkValidateItem =
    | OrganizationScheduleEventBulkValidateError
    | OrganizationScheduleEventBulkValidateSuccess;

export type OrganizationScheduleEventBulkValidateError = {
    status: 'error';
    parsedData: undefined;
    formattedInput: Record<string, string>;
    errors: Record<string, string>;
};

export type OrganizationScheduleEventBulkValidateSuccess = {
    status: 'success';
    parsedData: PostBulkOrganizationScheduleEvent;
    formattedInput: Record<string, string>;
    errors: undefined;
};

export type OrganizationTeam = {
    root_team_id: string;
    organization_id: string;
    status: string;
    name: string;
    sport: string;
    season_name: string;
    season_year: number;
    city?: string | null;
    state?: string | null;
    country?: string | null;
    staff_ids: string[];
    team_avatar_image?: string;
    proxy_team_id: string | null;
};

export interface OrganizationUserAssociations {
    organization_id: string;
    users: {
        user_id: string;
        association: string;
    }[];
}

export interface OrganizationLeaderboards {
    last_modified: string;
    player_stats: OrganizationLeaderboardStat[];
    includes: {
        glossary: {
            qualifications: {
                // Currently will only be betting/pitching/fielding, but setting up as generic
                [k: string]: string;
            };
        };
        categories: OrganizationLeaderboardCategory[];
        players?: {
            [k: string]: EdenPlayer;
        };
    };
}

export interface OrganizationLeaderboardStat {
    title: string;
    name: string;
    category: string;
    leaders: null | OrganizationLeaderboardLeader[];
}

export interface OrganizationLeaderboardLeader {
    team_id: string;
    player_id: string;
    value: string;
    ranking: number;
}

export interface OrganizationLeaderboardCategory {
    title: string;
    name: string;
}

export interface OrganizationResultsExport {
    fields?: OrganizationResultsExportField[];
    results?: Record<string, string>[];
    next_page_starts_at?: number;
}

export interface OrganizationResultsExportField {
    key: string;
    label: string;
}

export interface NotificationOption {
    should_notify: boolean;
    message: string | null;
}

export type InputContent = {
    [key: string]: Object;
} & {
    [InputContentType.NONE]: typeof NO_CONTENT;
    [InputContentType.POST_LOGIN_LINK]: typeof NO_CONTENT;

    // This uses any because both the old and new add-player actions send different objects to eden
    // TODO: When we remove old-add-player action, change any return type to object we send to eden from add-player
    [InputContentType.POST_PLAYER]: {
        player: {
            person?: {
                [k: string]: unknown;
            };
            first_name: string;
            last_name: string;
            team_id: string;
            id: string;
            number: string;
            bats?: {
                batting_side?: BattingSide;
                throwing_hand?: ThrowingHand;
            };
        };
    };
    [InputContentType.POST_BATS_PLAYER_ATTRIBUTES]: {
        bats_player_attributes: { player_id: string; batting_side?: BattingSide; throwing_hand?: ThrowingHand };
    };
    [InputContentType.PATCH_PLAYER_FAMILY_RELATIONSHIPS]: {
        updates: {
            add?: FamilyInfo[];
            remove?: UserIdOrEmail[];
        };
    };
    [InputContentType.PATCH_PLAYER]: { updates: PatchPlayer };
    [InputContentType.PATCH_BATS_PLAYER_ATTRIBUTES]: {
        updates: { batting_side?: BattingSide; throwing_hand?: ThrowingHand };
    };
    [InputContentType.PUT_TEAM_MANAGER]: {
        manager: { user_id?: string; email?: string };
        person?: { first_name: string; last_name: string; phone_number?: string };
    };
    [InputContentType.POST_CLIENT_AUTHENTICATE]: { client_id: string };
    [InputContentType.PUT_RSVP_RESPONSE]: {
        attending_status: 'no_response' | 'going' | 'not_going';
    };
    [InputContentType.POST_OPPONENT_TEAM]: {
        opponent_team: {
            root_team_id: string;
            owning_team_id: string;
            name: string;
        };
    };
    [InputContentType.POST_OPPONENT_TEAM_IMPORT]: {
        opponent_team_id: string;
        progenitor_team_id: string;
    };
    [InputContentType.POST_OPPONENT_TEAM_LEGACY_IMPORT]: {
        opponent_team_id: string;
        legacy_progenitor_team_id: string;
    };
    [InputContentType.POST_EVENT]: {
        event: {
            id: string;
            team_id: string;
            event_type: 'game' | 'practice' | 'other';
            sub_type: Array<string>;
            status: 'scheduled';
            title?: string;
            notes?: string;
            location: PostLocation;
            full_day: boolean;
            start: {
                datetime?: string;
                date?: string;
            };
            end: {
                datetime?: string;
                date?: string;
            };
            arrive?: {
                datetime?: string;
            };
            timezone?: string;
        };
        notification: NotificationOption;
        pregame_data?: {
            home_away?: HomeAway;
            opponent_name?: string;
            opponent_id?: string;
        };
    };
    [InputContentType.PATCH_EVENT]: {
        updates: {
            event: {
                sub_type?: Array<string>;
                status?: 'scheduled' | 'canceled';
                title?: string;
                notes?: string;
                location?: PatchLocation;
                full_day?: boolean;
                start?: {
                    datetime?: string;
                    date?: string;
                };
                end?: {
                    datetime?: string;
                    date?: string;
                };
                arrive?: {
                    datetime?: string;
                } | null;
                timezone?: string | null;
            };
            pregame_data?: {
                home_away?: HomeAway | null;
                opponent_name?: string | null;
                opponent_id?: string | null;
                lineup_id?: string | null;
            };
        };
        notification: NotificationOption;
    };
    [InputContentType.POST_EVENT_SERIES]: {
        event: {
            id: string;
            team_id: string;
            event_type: 'game' | 'practice' | 'other';
            sub_type: Array<string>;
            status: 'scheduled';
            title?: string;
            notes?: string;
            location: PostLocation;
            full_day: boolean;
            start: {
                datetime?: string;
                date?: string;
            };
            end: {
                datetime?: string;
                date?: string;
            };
            arrive?: {
                datetime?: string;
            };
            timezone?: string;
        };
        notification: NotificationOption;
        pregame_data?: {
            home_away?: HomeAway;
            opponent_name?: string;
            opponent_id?: string;
        };
        series_config: {
            id: string;
            team_id: string;
            rrule: string;
        };
    };
    [InputContentType.PATCH_EVENT_SERIES]: {
        notification: NotificationOption;
        immutable_identifiers: {
            event_type: 'game' | 'practice' | 'other';
            pivot_event_id: string;
        };
        updates: {
            pregame_data: {
                home_away?: HomeAway | null;
                opponent_name?: string;
                opponent_id?: string;
            };
            event: {
                status: 'scheduled' | 'canceled';
                sub_type?: Array<string>;
                title?: string;
                notes?: string;
                location?: PatchLocation;
                full_day?: boolean;
                start?: {
                    datetime?: string;
                    date?: string;
                };
                end?: {
                    datetime?: string;
                    date?: string;
                };
                arrive?: {
                    datetime?: string;
                };
                timezone?: string;
            };
            series_config?: { until: string };
        };
    };
    [InputContentType.PATCH_PASSWORD]: { current_password: string; updates: { password: string } };
    [InputContentType.PATCH_BATS_STARTING_LINEUP]: { updates: Omit<BatsStartingLineup, 'id'> };
    [InputContentType.POST_BATS_STARTING_LINEUP]: { lineup: BatsStartingLineup & { team_id: string } };
    [InputContentType.PUT_GAME_STAT_EDIT_COLLECTION]: {
        stat_edits: EdenStatEdit[];
    };
    [InputContentType.PATCH_ROSTER_ROLLOVER]: PatchTeamRollover;
    [InputContentType.POST_MAXPREPS_TEAM_IMPORT]: {
        team_id: string;
        import_component: MaxPrepsImportComponent;
    };

    [InputContentType.PATCH_ORGANIZATION_SCHEDULE_EVENT]: {
        updates: PatchOrganizationScheduleEvent;
        notification: NotificationOption;
    };

    [InputContentType.POST_ORGANIZATION_SCHEDULE_EVENT]: {
        organization_schedule_event: PostOrganizationScheduleEvent;
        notification: NotificationOption;
    };
    [InputContentType.POST_ORGANIZATION_SCHEDULE_EVENT_BULK]: PostBulkOrganizationScheduleEvent[];
    [InputContentType.POST_ORGANIZATION_SCHEDULE_EVENT_BULK_VALIDATE]: PostOrganizationScheduleEventBulkValidate;
    [InputContentType.POST_ORGANIZATION_TEAMS]: string[];

    [InputContentType.PATCH_STREAM_ASSET_METADATA]: {
        updates: {
            hidden: boolean;
        };
    };
    [InputContentType.PATCH_OPPONENT_TEAM]: {
        updates: {
            name?: string;
            is_hidden?: boolean;
        };
    };
};

export type Location = {
    name?: string | null;
    place_id?: string | null;
    coordinates?: {
        latitude?: number | null;
        longitude?: number | null;
    } | null;
    google_place_details?: GooglePlaceDetails | null;
    address?: string[] | null;
};

export type GooglePlaceDetails = {
    id: string;
    lat_long?: {
        lat: number;
        long: number;
    };
    address?: string;
    address_object?: {
        street_number?: string;
        street_name?: string;
        city?: string;
        state?: string;
        country?: string;
        postal_code?: string;
    };
    location_name?: string;
    types: string[];
};

export type User = {
    id: string;
    password: string;
    first_name: string;
    last_name: string;
    email: string;
    registration_date: string;
    status: 'invited' | 'active' | 'active-confirmed';
    is_bats_account_linked: boolean;
};

export type Subscription = {
    type: string;
    provider_type: string;
    is_trial: boolean;
    end_date: string;
    access_level: string;
    billing_cycle: string;
    amount_in_cents: number;
    provider_details?: {
        will_renew: boolean;
        was_terminated_by_staff: boolean;
        was_terminated_by_provider: boolean;
    };
    is_gc_classic?: boolean;
};

export type SubscriptionInformation = {
    best_subscription: Subscription | null;
    highest_access_level: string | null;
    is_free_trial_eligible: boolean;
};

export type TeamUser = {
    id: string;
    status: string;
    first_name: string;
    last_name: string;
    email?: string;
    phone_number?: string | null; // we've seen nulls despite Eden saying otherwise
    chat_id?: string;
};

export type EdenPlayer = {
    id: string;
    first_name: string;
    last_name: string;
    number: string;
    status: 'active' | 'merged' | 'removed';
    user_id?: string | null;
    team_id: string;
    bats?: { batting_side: BattingSide | null; throwing_hand: ThrowingHand | null } | null;
};

export type AudienceType = 'players_family' | 'players_family_fans';

export type PlayerRole = 'batter' | 'fielder' | 'pitcher' | 'runner';

export type PlayType =
    | 'single'
    | 'double'
    | 'triple'
    | 'home_run'
    | 'batter_out'
    | 'batter_out_advance_runners'
    | 'double_play'
    | 'triple_play'
    | 'fielders_choice'
    | 'fielders_choice_double_play'
    | 'sacrifice_fly'
    | 'sacrifice_bunt'
    | 'infield_fly'
    | 'foul_tip_out'
    | 'strikeout'
    | 'walk'
    | 'hit_by_pitch'
    | 'catcher_interference'
    | 'error'
    | 'dropped_third_strike'
    | 'dropped_third_strike_batter_out'
    | 'other_out'
    | 'sacrifice_fly'
    | 'sacrifice_fly_error'
    | 'sacrifice_bunt'
    | 'sacrifice_bunt_error'
    | 'stole_base'
    | 'caught_stealing'
    | 'picked_off'
    | 'passed_ball'
    | 'wild_pitch'
    | 'other_advance'
    | 'offensive_interference'
    | 'made2'
    | 'made3'
    | 'goal'
    | 'conversion'
    | 'try'
    | 'drop_goal'
    | 'penalty_goal';

export interface PlayerVideoClip {
    clip_metadata_id: string;
    audience_type: AudienceType;
    event_id: string;
    play_type: PlayType;
    player_id: string;
    player_role: PlayerRole;
    team_id: string;

    duration?: number | null;
    thumbnail_url?: string | null;
    stream_id: string;
    timestamp: string; //Date
}

export interface VideoClip {
    url: string;
    player_clip_metadata_id: string;
    cookies: { [key: string]: string };
}

export type HomeAway = 'home' | 'away';

export type RSVPResponse = {
    event_id: string;
    responding_user_id?: never;
    attending_status: 'no_response' | 'going' | 'not_going';
    attending_id_type?: 'user' | 'player';
    attending_id?: string | null;
};

export type Event = {
    event: {
        id: string;
        event_type: 'game' | 'practice' | 'other';
        sub_type: 'scrimmages'[];
        status: 'scheduled' | 'canceled';
        full_day: boolean;
        team_id: string;
        start: EdenDateOrDateTime;
        end: EdenDateOrDateTime;
        arrive?: { datetime: string } | null;
        timezone?: string | null;
        location?: Location | null;
        notes?: string;
        title?: string | null;
        series_id?: string | null;
        organization_id?: string | null;
    };
    pregame_data?: {
        game_id: string;
        opponent_name?: string;
        opponent_id?: string | null;
        home_away?: HomeAway | null;
        lineup_id?: string | null;
    };
};

export interface EdenGameStreamViewerPayloadLite {
    stream_id: string;
    latest_events: EdenGameStreamEvent[];
    all_event_data_ids: string[];
    marker: string;
}

export type GameSummary = GameSummary_0_1_0;
export type AssetPlaybackData = InferType<typeof AssetPlaybackDataSchema>;
export type APIOpponentTeam = InferType<typeof APIOpponentTeamSchema>;
export type StreamAssetMetadataWithTimeStamps = InferType<typeof streamAssetMetadataWithTimeStampsSchema>;
export type APIAthleteProfile = InferType<typeof APIAthleteProfileSchema>;
export type APIAthleteProfileSportAttributes = InferType<typeof APIAthleteProfileSportAttributesSchema>;
export type APIAthleteProfileClips = InferType<typeof APIAthleteProfileClipsListSchema>;
export type APIAthleteProfileCareerStats = InferType<typeof APIAthleteProfileCareerStatsSchema>;
export type OutputContent = {
    [key: string]: Object;
} & {
    [OutputContentType.NONE]: typeof NO_CONTENT;
    [OutputContentType.PLAYER]: EdenPlayer;
    [OutputContentType.TEAM_FAN]: {
        user_id: string;
        first_name: string;
        last_name: string;
    };
    [OutputContentType.USER_TEAM_ASSOCIATIONS]: UserTeamAssociation;
    [OutputContentType.USER_PLAYER_RELATIONSHIP]: UserPlayerRelationship;
    [OutputContentType.TEAM_USER]: TeamUser;
    [OutputContentType.CLIENT_TOKEN]: { token: string };
    [OutputContentType.USER_TOKEN]: { token: string };
    [OutputContentType.TEAM]: Team;
    [OutputContentType.TEAM_AVATAR_IMAGE]: { full_media_url: string };
    [OutputContentType.USER]: User;
    [OutputContentType.SUBSCRIPTION_INFORMATION]: SubscriptionInformation;
    [OutputContentType.EVENT]: Event;
    [OutputContentType.RSVP_RESPONSE]: RSVPResponse;
    [OutputContentType.EVENT_SERIES]: {
        id: string;
        team_id: string;
        rrule: string;
        meta_seq: string;
        created_at: string;
        updated_at: string;
    };
    [OutputContentType.TEAM_SEASON_STATS]: {
        id: string;
        team_id: string;
        stats_data: EdenStatsDoc;
    };
    [OutputContentType.PLAYER_STATS]: {
        event_id: string;
        stream_id: string;
        game_date: string;
        player_stats: { stats: EdenStats };
        cumulative_stats: { stats: EdenStats };
        offensive_spray_charts: SprayChartData[] | null;
        defensive_spray_charts: SprayChartData[] | null;
    };
    [OutputContentType.PLAYER_GAME_STATS]: {
        event_id: string;
        stream_id: string;
        team_id?: string;
        derivation_hash: string;
        player_stats: EdenStatsDoc;
        cumulative_player_stats: EdenStatsDoc;
        spray_chart_data?: SprayChartData;
    };
    [OutputContentType.GAME_SUMMARY]: GameSummary;
    [OutputContentType.OPPONENT_TEAM_IMPORT_SEARCH_RESULT]: {
        hits: {
            id: string;
            name: string;
            sport: string;
            team_season: { season: string; year: number };
            competition_level: string;
            age_group: string;
            number_of_players: number;
            location: {
                city: string | null;
                state: string | null;
                country: string;
            };
            is_legacy_team: boolean;
            is_orphan: boolean;
            avatar_url?: string | null;
        }[];
        total_count: number;
    };
    [OutputContentType.OPPONENT_TEAM_ID]: {
        opponent_team_id: string;
    };
    [OutputContentType.EVENT_BEST_STREAM_ID]: {
        game_stream_id: string;
    };
    // minimal mapping - just what we need right now.
    [OutputContentType.GAME_STREAM]: {
        id: string;
        home_away: HomeAway;
    };
    [OutputContentType.GAME_STREAM_EVENT]: EdenGameStreamEvent;
    [OutputContentType.GAME_STREAM_VIEWER_PAYLOAD_LITE]: EdenGameStreamViewerPayloadLite;
    // in the output type entries is always defined.
    [OutputContentType.BATS_STARTING_LINEUP]: APIBatsStartingLineup;
    [OutputContentType.LATEST_BATS_STARTING_LINEUP]: {
        latest_lineup: APIBatsStartingLineup | null;
    };

    [OutputContentType.GAME_STAT_EDIT_COLLECTION]: {
        sabertooth_stat_edits: EdenSabertoothEvent[];
    };

    [OutputContentType.MAXPREPS_SCHOOL_SEARCH_RESULTS]: {
        hits: {
            id: string;
            name: string;
            city: string;
            state: string;
        }[];
        total_count: number;
    };
    [OutputContentType.MAXPREPS_IMPORT_RESULT]: {
        player_count: number;
        game_count: number;
    };

    [OutputContentType.SCHEDULE_EVENT_VIDEO_STREAM]: {
        disabled: boolean;
        is_muted: boolean;
        is_playable?: boolean | null;
        live_at?: string | null;
        playable_at?: string | null;
        playback_url: string;
        publish_url: string;
        schedule_event_id: string;
        stream_id: string;
        thumbnail_url?: string | null;
        active_asset_id?: string;
        active_asset_playback_url?: string | null;
        audience_type?: AudienceType | null;
        status: 'active' | 'ended';
        team_id: string;
        user_id?: string | null;
        person_id: string;
        viewer_count: number;
    };

    [OutputContentType.ORGANIZATION]: Organization;
    [OutputContentType.ORGANIZATION_WITH_ROLE]: OrganizationWithRole;
    [OutputContentType.ORGANIZATION_TEAM]: OrganizationTeam;
    [OutputContentType.ORGANIZATION_USER_ASSOCIATIONS]: OrganizationUserAssociations;
    [OutputContentType.ORGANIZATION_SCHEDULE_EVENT]: OrganizationScheduleEvent;
    [OutputContentType.ORGANIZATION_SCHEDULE_EVENT_BULK_UPLOAD_TEMPLATE]: OrganizationScheduleEventBulkUploadTemplate;
    [OutputContentType.ORGANIZATION_SCHEDULE_EVENT_BULK_VALIDATE]: OrganizationScheduleEventBulkValidateResult;
    [OutputContentType.ORGANIZATION_LEADERBOARDS]: OrganizationLeaderboards;
    [OutputContentType.ORGANIZATION_SCHEDULE_EVENT_RESULTS_EXPORT]: OrganizationResultsExport;

    [OutputContentType.VIDEO_CLIP_ASSET_METADATA]: PlayerVideoClip;
    [OutputContentType.VIDEO_CLIP]: VideoClip;
    [OutputContentType.VIDEO_STREAM_ASSET_METADATA]: StreamAssetMetadataWithTimeStamps;
    [OutputContentType.VIDEO_STREAM_ASSET_PLAYBACK_DATA]: AssetPlaybackData;
    [OutputContentType.OPPONENT_TEAM]: APIOpponentTeam;
    [OutputContentType.ATHLETE_PROFILE_PUBLIC]: Response;
    [OutputContentType.ATHLETE_PROFILE_CLIPS_PUBLIC]: APIAthleteProfileClips;
    [OutputContentType.ATHLETE_PROFILE_CAREER_STATS_PUBLIC]: Response;

    [OutputContentType.PUBLIC_TEAM_PROFILE]: {
        id: string;
        name: string;
        sport: string;
        avatar_url: string;
        location: { city: string; state: string; country: string };
        team_season: {
            season: string;
            year: number;
            record: { win: number; loss: number; tie: number };
        };
    };
    [OutputContentType.TEAM_PUBLIC_PROFILE_ID]: {
        id: string;
    };
};
